import { memo, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import FocusLock from 'react-focus-lock';
import { throttle } from 'underscore';

import OptionalComponent from '@ui/optional-component/optional-component';
import Portal from '@ui/portal/portal';
import WaitingIndicator from '@ui/waiting-indicator/waiting-indicator';
import { MODAL_ANIMATION_TIME, ModalProps } from './types';
import randomString from '@/utils/random-string';
import { isDefined } from '@/utils/is-defined';
import * as EventEmitter from '@/utils/event-emitter';
import { useStableCallback } from '@/utils/use-stable-callback';
import useModalEmitters from './use-modal-emitters';

import styles from './modal.module.css';

const Modal = memo(
  ({
    id: propsId,
    open: propsOpen,
    contentWrapperClassName,
    contentClassName,
    portalClassName,
    modalClassName,
    footerClassName,
    backgroundColor,
    onRequestClose,
    animation = 'slide-up',
    size = 'medium',
    withZoom = true,
    showBackdrop = true,
    children,
    RenderHeaderContent,
    RenderFooterContent,
    customSize,
    delayContentRender = 0,
    showLoadingIndicator = false,
    delayRenderLoadingIndicator = 0,
    onTransitionOpening,
    onTransitionOpened,
    onTransitionClosing,
    onTransitionClosed,
    fadeTransition = true,
    transitionDuration = MODAL_ANIMATION_TIME,
    transitionTimingFunction = 'ease-in-out',
    animateHeightTransition,
    'data-component': dataComponent = 'Modal',
    focusLockProps,
    design,
    drawerMode,
    onBodyScroll,
    onBodyScrollToBottom,
    preventClickAway,
  }: ModalProps) => {
    // Controlled: parent passes `open` prop. Uncontrolled: events via useModal drive state.
    // These two modes are mutually exclusive — see ModalProps discriminated union in types.ts.
    const isControlled = propsOpen !== undefined;
    const [eventOpen, setEventOpen] = useState(false);
    const targetOpen = isControlled ? !!propsOpen : eventOpen;

    const id = useMemo(() => propsId || randomString(7), [propsId]);

    const [showModal, setShowModal] = useState(!!propsOpen);
    const [closing, setClosing] = useState(false);
    // There's a scenario were onTransitionOpened is not called when the modal is opened by default on first render,
    // this is a workaround to ensure that onTransitionOpened is in that scenario.
    // Do not update this value anywhere else in the component. It's a set and forget value.
    const isDefaultOpen = useRef(!!propsOpen);
    const hasOpenedAtLeastOnceRef = useRef(false);

    const onTransitionOpeningRef = useStableCallback(onTransitionOpening);
    const onTransitionOpenedRef = useStableCallback(onTransitionOpened);
    const onTransitionClosingRef = useStableCallback(onTransitionClosing);
    const onTransitionClosedRef = useStableCallback(onTransitionClosed);
    const onBodyScrollRef = useStableCallback(onBodyScroll);
    const onBodyScrollToBottomRef = useStableCallback(onBodyScrollToBottom);

    const portalRef = useRef<HTMLDivElement>(null);
    const bodyRef = useRef<HTMLDivElement>(null);

    const closeTimeout = useRef<NodeJS.Timeout | null>(null);
    const openedCheckTimer = useRef<NodeJS.Timeout | null>(null);

    useModalEmitters({
      id,
      open: !!(targetOpen || showModal),
      setShowModal: setEventOpen,
    });

    // listen for close requests from other components
    useEffect(() => {
      const closeModalId = `${id}::close-modal`;
      const handleClose = () => {
        onRequestClose?.();
      };
      EventEmitter.addEventListener(closeModalId, handleClose);

      return () => EventEmitter.removeEventListener(closeModalId, handleClose);
    }, [id, onRequestClose]);

    useEffect(() => {
      const portalEl = portalRef.current;
      if (!portalEl) return;

      if (closeTimeout.current) {
        clearTimeout(closeTimeout.current);
      }

      if (targetOpen) {
        // timeout required on setShowModal because transition methods make first render lose their animations.
        setTimeout(() => setShowModal(true));
        portalEl.classList.add(`${[styles['open']]}`);
        onTransitionOpeningRef.current?.();
        // if the modal is opened by default, we need to call onTransitionOpened
        if (isDefaultOpen.current && !hasOpenedAtLeastOnceRef.current) {
          onTransitionOpenedRef.current?.();
        }
        hasOpenedAtLeastOnceRef.current = true;
      } else {
        if (hasOpenedAtLeastOnceRef.current) {
          onTransitionClosingRef.current?.();
          setClosing(true);
        }
        closeTimeout.current = setTimeout(() => {
          setShowModal(false);
          portalEl.classList.remove(`${[styles['open']]}`);
          if (hasOpenedAtLeastOnceRef.current) {
            onTransitionClosedRef.current?.();
            setClosing(false);
          }
        }, transitionDuration);
      }

      return () => {
        if (closeTimeout.current) {
          clearTimeout(closeTimeout.current);
        }
      };
      // only run this effect when targetOpen changes, all the transition methods will be called at that time anyway
      // and we also don't want to run this effect at any other time than when targetOpen changes
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetOpen]);

    // call onTransitionOpened after opened transition completes (do not use ontransitionend event listener, it's too inconsistent)
    useEffect(() => {
      if (openedCheckTimer.current) {
        clearTimeout(openedCheckTimer.current);
      }
      if (!targetOpen || !showModal) return;

      openedCheckTimer.current = setTimeout(() => {
        onTransitionOpenedRef.current?.();
      }, transitionDuration);

      return () => {
        if (openedCheckTimer.current) {
          clearTimeout(openedCheckTimer.current);
        }
      };
      // onTransitionOpenedRef is a stable ref — excluding it intentionally
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetOpen, showModal, transitionDuration]);

    useEffect(() => {
      if (!onRequestClose) return;

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          if (!targetOpen) return;
          e.stopPropagation(); // prevent other escape key events from being triggered
          onRequestClose();
        }
      };

      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }, [onRequestClose, targetOpen]);

    useEffect(() => {
      // This effect is used to set the data-component attribute on the portal element
      // This is useful for testing purposes
      const portalEl = portalRef.current;
      if (portalEl) {
        portalEl.setAttribute('data-component', dataComponent);
      }
    }, [dataComponent]);

    const handleBodyScroll = useRef(
      throttle((e: React.UIEvent<HTMLDivElement>) => {
        const target = e.target as HTMLDivElement;
        onBodyScrollRef.current?.();
        if (target.scrollTop + target.clientHeight >= target.scrollHeight - 100) {
          onBodyScrollToBottomRef.current?.();
        }
      }, 100)
    ).current;

    const modalBodyStyle = useMemo(
      () =>
        ({
          ...(isDefined(customSize?.width) ? { '--modalBodyCustomWidth': customSize.width } : {}),
          ...(isDefined(customSize?.maxWidth) ? { '--modalBodyCustomMaxWidth': customSize.maxWidth } : {}),
          ...(isDefined(customSize?.height) ? { '--modalBodyCustomHeight': customSize.height } : {}),
          ...(isDefined(customSize?.maxHeight) ? { '--modalBodyCustomMaxHeight': customSize.maxHeight } : {}),
          ...(isDefined(customSize?.minHeight) ? { '--modalBodyCustomMinHeight': customSize.minHeight } : {}),
          ...(isDefined(customSize?.minWidth) ? { '--modalBodyCustomMinWidth': customSize.minWidth } : {}),
          ...(isDefined(customSize?.tabletMinHeight)
            ? { '--modalBodyCustomTabletMinHeight': customSize.tabletMinHeight }
            : {}),
          ...(isDefined(customSize?.mobileMinHeight)
            ? { '--modalBodyCustomMobileMinHeight': customSize.mobileMinHeight }
            : {}),
          ...(backgroundColor ? { '--modalBodyCustomBackgroundColor': backgroundColor } : {}),
          '--modalInitialOpacity': fadeTransition ? 0 : 1,
          '--modaltransitionDuration': `${transitionDuration ?? 300}ms`,
          '--modalTransitionTimingFunction': transitionTimingFunction ?? 'ease-in-out',
        }) as React.CSSProperties,
      [
        customSize?.width,
        customSize?.maxWidth,
        customSize?.height,
        customSize?.maxHeight,
        customSize?.minHeight,
        customSize?.minWidth,
        customSize?.tabletMinHeight,
        customSize?.mobileMinHeight,
        backgroundColor,
        fadeTransition,
        transitionDuration,
        transitionTimingFunction,
      ]
    );

    return (
      <Portal
        id={id}
        className={classNames('global-modal-portal', styles['modal-portal'], portalClassName)}
        aria-hidden={!targetOpen && !showModal}
        ref={portalRef}
      >
        <FocusLock
          returnFocus
          disabled={!targetOpen && !showModal} /* without this focus trap doesn't work */
          className={classNames(
            'flex-column jc-center ai-center',
            styles['modal-focus-lock'],
            styles['size-' + (size || 'medium')],
            {
              [styles['open']]: targetOpen,
              [styles['show']]: showModal || closing,
            }
          )}
          {...focusLockProps}
        >
          <OptionalComponent display={showBackdrop}>
            <div
              role="presentation"
              className={classNames(styles['modal-backdrop'], { [styles.transparent]: drawerMode })}
              onClick={preventClickAway ? undefined : onRequestClose}
            />
          </OptionalComponent>
          <div
            role="dialog"
            aria-modal="true"
            inert={!targetOpen && !closing}
            ref={bodyRef}
            id={`${id}-modal-body`}
            className={classNames(
              'global-modal-body',
              styles['modal-body'],
              styles[animation || 'slide-up'],
              styles['size-' + (size || 'medium')],
              {
                [styles['zoom']]: withZoom,
                [styles['custom-size']]: !!customSize && Object.keys(customSize).length > 0,
                [styles['animate-height-transition']]: animateHeightTransition,
                ...(design ? { [design]: design } : {}),
                [styles.drawer]: drawerMode,
              },
              modalClassName
            )}
            style={modalBodyStyle}
          >
            <OptionalComponent delayRender={delayRenderLoadingIndicator} display={targetOpen && showLoadingIndicator}>
              <div className="flex-column jc-center ai-center height-100-percent">
                <WaitingIndicator />
              </div>
            </OptionalComponent>
            {/* intentionally unmounting content when modal is closed so that content is forced to re-render when re-opening */}
            {/* delayContentRender is used to prevent jank when async calls render before the animation has complete */}
            <OptionalComponent
              delayRender={delayContentRender}
              display={!showLoadingIndicator && (showModal || targetOpen)}
            >
              <div
                className={classNames(
                  'flex-column',
                  'height-100-percent',
                  styles['modal-content-wrapper'],
                  contentWrapperClassName
                )}
              >
                {RenderHeaderContent ? (
                  <div className={styles['modal-header']}>
                    {typeof RenderHeaderContent === 'function' ? <RenderHeaderContent /> : RenderHeaderContent}
                  </div>
                ) : null}

                <div className={`${contentClassName || ''} ${styles['modal-content']}`} onScroll={handleBodyScroll}>
                  {children}
                </div>

                {RenderFooterContent ? (
                  <div className={footerClassName || ''}>
                    {typeof RenderFooterContent === 'function' ? <RenderFooterContent /> : RenderFooterContent}
                  </div>
                ) : null}
              </div>
            </OptionalComponent>
          </div>
        </FocusLock>
      </Portal>
    );
  }
);

export default Modal;
